import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ChevronDown, ChevronRight, Download, Lightbulb, CheckCircle2, AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from "xlsx";
import type { Variedade } from "@shared/schema";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { useVariedadesDoCacho } from "@/hooks/use-variedades-cacho";
type CicloDetalhe = {
  cicloId: string;
  codigo: string;
  corNome: string;
  hexColor: string;
  anoPrevisto: number | null;
  realizadoSemanas: number | null;
  cachosPlanejados: number;
  cachosColhidos: number;
  percentualRealizado: number;
  parcial: boolean;
};

type GrupoPrevistoRealizado = {
  variedadeId: string;
  variedadeNome: string;
  mesNum: number;
  mesLabel: string;
  semanasPrevistas: number;
  realizadoSemanas: number | null;
  desvioSemanas: number | null;
  numCiclos: number;
  sugestao: number | null;
  cachosPlanejados: number;
  cachosColhidos: number;
  percentualRealizado: number;
  ciclos: CicloDetalhe[];
};

// Classe de cor para o desvio: verde |d|<0.5, âmbar até 1.0, vermelho acima.
function desvioClass(d: number | null): string {
  if (d === null) return "";
  const a = Math.abs(d);
  if (a < 0.5) return "text-sucesso";
  if (a <= 1) return "text-atencao";
  return "text-erro";
}

// Classe de cor para % realizado: verde ≥90, âmbar 75-89, vermelho <75.
function pctClass(p: number): string {
  if (p >= 90) return "text-sucesso";
  if (p >= 75) return "text-atencao";
  return "text-erro";
}

function fmtDesvio(d: number | null): string {
  if (d === null) return "—";
  return (d > 0 ? "+" : "") + d.toFixed(1).replace(".", ",");
}

function fmtSemanas(s: number | null): string {
  if (s === null) return "—";
  return s.toFixed(1).replace(".", ",");
}

const MESES_ABREV_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

// Estima a faixa de meses de colheita a partir do mês do corte (1-12) e das
// semanas de maturação. Usa um ano de referência não bissexto (2025) — só
// interessa o mês resultante, então a virada de ano é tratada naturalmente.
function estimarFaixaColheita(mesNum: number, semanas: number | null): string {
  if (!mesNum || mesNum < 1 || semanas === null || semanas <= 0) return "—";
  const ms = semanas * 7 * 86400000;
  const ANO_REF = 2025; // não bissexto
  // início: dia 1 do mês do corte + semanas; fim: último dia do mês do corte + semanas
  const inicio = new Date(Date.UTC(ANO_REF, mesNum - 1, 1) + ms);
  const ultimoDia = new Date(Date.UTC(ANO_REF, mesNum, 0)).getUTCDate();
  const fim = new Date(Date.UTC(ANO_REF, mesNum - 1, ultimoDia) + ms);
  const mesInicio = MESES_ABREV_PT[inicio.getUTCMonth()];
  const mesFim = MESES_ABREV_PT[fim.getUTCMonth()];
  return mesInicio === mesFim ? mesInicio : `${mesInicio}–${mesFim}`;
}

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_PREVISTO = { ano: "tudo", variedade: "todas", incluirAndamento: false };

export default function RelatoriosPrevistoRealizadoPage() {
  const { toast } = useToast();
  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_PREVISTO);
  const anoFilter = filtros.ano;
  const variedadeFilter = filtros.variedade;
  const incluirAndamento = filtros.incluirAndamento;

  // Só banana: Cacho não tem ciclo de citros, então Pêra Rio no seletor só
  // podia devolver relatório vazio. Ver `useVariedadesDoCacho` (fail-open).
  const { variedades } = useVariedadesDoCacho();

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (anoFilter !== "tudo") params.set("ano", anoFilter);
    if (variedadeFilter !== "todas") params.set("variedadeId", variedadeFilter);
    if (incluirAndamento) params.set("incluirAndamento", "true");
    return params.toString();
  }, [anoFilter, variedadeFilter, incluirAndamento]);

  const { data: grupos, isLoading } = useQuery<GrupoPrevistoRealizado[]>({
    queryKey: [`/api/relatorios/previsto-realizado${queryString ? `?${queryString}` : ""}`],
  });

  // Anos disponíveis para o filtro (a partir dos ciclos retornados, união de anos).
  const anosDisponiveis = useMemo(() => {
    const anos = new Set<number>();
    grupos?.forEach(g => g.ciclos.forEach(c => {
      if (c.anoPrevisto) anos.add(c.anoPrevisto);
    }));
    return Array.from(anos).sort((a, b) => b - a);
  }, [grupos]);

  const handleExportExcel = () => {
    if (!grupos || grupos.length === 0) return;
    try {
      const exportData = grupos.map(g => ({
        "Variedade": g.variedadeNome,
        "Mês do Corte": g.mesLabel,
        "Colheita Estimada": estimarFaixaColheita(g.mesNum, g.realizadoSemanas ?? g.semanasPrevistas),
        "Prev (sem)": g.semanasPrevistas,
        "Realizado (sem)": g.realizadoSemanas ?? "-",
        "Desvio (sem)": g.desvioSemanas ?? "-",
        "Ciclos": g.numCiclos,
        "Planejado": g.cachosPlanejados,
        "Colhido": g.cachosColhidos,
        "% Real": g.percentualRealizado,
        "Saldo": g.cachosPlanejados - g.cachosColhidos,
        "Sugestão (sem)": g.sugestao ?? "-",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "PrevistoRealizado");
      ws["!cols"] = [15, 13, 16, 11, 14, 12, 8, 12, 10, 8, 10, 13].map(w => ({ wch: w }));

      const filename = `previsto_realizado_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast({ title: "Exportado com sucesso", description: `Arquivo ${filename} foi baixado` });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast({ title: "Erro ao exportar", description: "Não foi possível gerar o arquivo", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start gap-3">
        <Link href="/cacho/relatorios">
          <Button variant="ghost" size="icon" data-testid="button-voltar-relatorios">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <PageHeader
            titulo="Previsto × realizado"
            descricao="Maturação prevista vs realizada por variedade e mês do corte do mangará · calibra o cadastro de Variedades"
            acao={
              grupos && grupos.length > 0 ? (
                <Button variant="outline" className={ALVO_TOQUE_BOTAO} onClick={handleExportExcel} data-testid="button-export-excel">
                  <Download className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Exportar</span>
                </Button>
              ) : undefined
            }
          />
        </div>
      </div>

      {/* Toolbar do Índice: sem card, sem moldura. */}
      <PageToolbar
        fim={
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {grupos?.length || 0} {(grupos?.length || 0) === 1 ? "grupo" : "grupos"}
          </span>
        }
      >
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Ano</Label>
        <Select value={anoFilter} onValueChange={v => definir({ ano: v })}>
          <SelectTrigger className="w-[140px] shrink-0" data-testid="select-ano">
            <SelectValue placeholder="Tudo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tudo">Tudo</SelectItem>
            {anosDisponiveis.map(ano => (
              <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Variedade</Label>
        <Select value={variedadeFilter} onValueChange={v => definir({ variedade: v })}>
          <SelectTrigger className="w-[160px] shrink-0" data-testid="select-variedade">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            {variedades?.map(v => (
              <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 shrink-0">
          <Switch
            id="incluir-andamento"
            checked={incluirAndamento}
            onCheckedChange={v => definir({ incluirAndamento: v })}
            data-testid="switch-incluir-andamento"
          />
          <Label htmlFor="incluir-andamento" className="text-sm cursor-pointer whitespace-nowrap">
            Incluir ciclos em andamento
          </Label>
        </div>
      </PageToolbar>

      {incluirAndamento && (
        <div className="flex items-start gap-2 rounded-md border border-atencao/30 bg-atencao-suave p-3 text-xs text-muted-foreground">
          <AlertTriangle className="h-4 w-4 text-atencao flex-shrink-0 mt-0.5" />
          <span>
            Ciclos em andamento aparecem como <b>Parciais</b>: a média deles tende a ser menor que a real
            (os cachos precoces saem primeiro) e <b>não alimenta sugestões nem as médias de maturação</b>.
          </span>
        </div>
      )}

      {isLoading ? (
        <PageSkeleton variante="cards" linhas={3} />
      ) : !grupos || grupos.length === 0 ? (
        <EmptyState
          titulo="Nenhum dado para os filtros selecionados"
          descricao="A comparação sai dos ciclos que já tiveram colheita — amplie o ano ou a variedade acima."
          testId="empty-previsto-realizado"
        />
      ) : (
        <div className="space-y-3">
          {grupos.map(grupo => (
            <GrupoCard key={`${grupo.variedadeId}-${grupo.mesNum}`} grupo={grupo} />
          ))}
        </div>
      )}
    </div>
  );
}

function GrupoCard({ grupo }: { grupo: GrupoPrevistoRealizado }) {
  const [isOpen, setIsOpen] = useState(false);
  const temParcial = grupo.ciclos.some(c => c.parcial);
  const saldo = grupo.cachosPlanejados - grupo.cachosColhidos;
  const faixaColheita = estimarFaixaColheita(grupo.mesNum, grupo.realizadoSemanas ?? grupo.semanasPrevistas);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <DataCard
        titulo={`Corte: ${grupo.mesLabel}`}
        meta={
          <>
            {/* Variedade é CATEGORIA: outline neutro. */}
            <Badge variant="outline" className="text-xs font-normal mr-2">{grupo.variedadeNome}</Badge>
            Colheita ≈ {faixaColheita}
          </>
        }
        /* "Parcial" É estado: o grupo tem ciclo ainda em andamento, e o número
           vai mudar. Tom `info`, como em qualquer outro "em andamento". */
        estado={temParcial ? <StatusBadge tom="info" ponto>Parcial</StatusBadge> : undefined}
        acao={
          isOpen ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )
        }
        className={temParcial ? "border-dashed" : ""}
        testId={`grupo-${grupo.variedadeId}-${grupo.mesNum}`}
      >
        <CollapsibleTrigger asChild>
          <div className="p-3 md:p-4 cursor-pointer hover-elevate">

            {/* Maturação */}
            <div className="mb-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                Maturação (semanas)
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <Metric label="Previsto" value={`${grupo.semanasPrevistas} sem`} />
                <Metric label="Realizado" value={`${fmtSemanas(grupo.realizadoSemanas)} sem`} />
                <Metric
                  label="Desvio"
                  value={fmtDesvio(grupo.desvioSemanas)}
                  className={desvioClass(grupo.desvioSemanas)}
                  testId={`desvio-${grupo.variedadeId}-${grupo.mesNum}`}
                />
                <Metric label="Ciclos" value={String(grupo.numCiclos)} />
              </div>
            </div>

            {/* Quantidade */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
                Quantidade (cachos)
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <Metric label="Previsto" value={grupo.cachosPlanejados.toLocaleString("pt-BR")} />
                <Metric label="Colhido" value={grupo.cachosColhidos.toLocaleString("pt-BR")} />
                <Metric
                  label="% Real"
                  value={`${grupo.percentualRealizado}%`}
                  className={temParcial ? "" : pctClass(grupo.percentualRealizado)}
                />
                <Metric label="Saldo" value={saldo.toLocaleString("pt-BR")} />
              </div>
            </div>

            {/* Sugestão / status */}
            {grupo.sugestao !== null ? (
              <div
                className="mt-3 flex items-center gap-2 flex-wrap rounded-md border border-atencao/30 bg-atencao-suave p-2.5 text-sm"
                data-testid={`sugestao-${grupo.variedadeId}-${grupo.mesNum}`}
              >
                <Lightbulb className="h-4 w-4 text-atencao flex-shrink-0" />
                <span>
                  Sugestão: ajustar <b>{grupo.variedadeNome} · corte de {grupo.mesLabel}</b> de {grupo.semanasPrevistas}{" "}
                  para <b className="text-atencao">{grupo.sugestao} semanas</b>
                </span>
                <Link
                  href="/cacho/variedades"
                  className="ml-auto text-xs font-bold text-sucesso hover:underline whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  Ajustar cadastro →
                </Link>
              </div>
            ) : !temParcial && grupo.realizadoSemanas !== null ? (
              <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-sucesso">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Sem ajuste sugerido para o cadastro
              </div>
            ) : null}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="pt-0 pb-3 md:pb-4 px-3 md:px-4">
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs text-muted-foreground font-medium mb-2">Detalhe por ciclo / fita:</p>
              {grupo.ciclos.map((c) => (
                <div
                  key={c.cicloId}
                  className="flex items-center gap-3 flex-wrap p-2 rounded-md bg-muted/30 text-xs"
                  data-testid={`ciclo-${c.cicloId}`}
                >
                  <div className="w-3 h-3 rounded-full border flex-shrink-0" style={{ backgroundColor: c.hexColor }} />
                  <span className="font-mono font-semibold">{c.codigo}</span>
                  <span className="text-muted-foreground">{c.corNome} · {c.anoPrevisto ?? "—"}</span>
                  {c.parcial && (
                    <Badge variant="secondary" className="text-[10px] bg-info-suave text-info">
                      Parcial
                    </Badge>
                  )}
                  <div className="ml-auto flex items-center gap-3">
                    <span>Real: <b className="font-mono">{fmtSemanas(c.realizadoSemanas)} sem</b></span>
                    <span className="font-mono">
                      <b>{c.cachosColhidos}</b>/<b>{c.cachosPlanejados}</b>
                    </span>
                    <span className={`font-mono font-semibold ${c.parcial ? "" : pctClass(c.percentualRealizado)}`}>
                      {c.percentualRealizado}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </DataCard>
    </Collapsible>
  );
}

function Metric({ label, value, className, testId }: { label: string; value: string; className?: string; testId?: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-[10.5px] uppercase">{label}</p>
      <p className={`font-mono text-xs tabular-nums font-bold ${className || ""}`} data-testid={testId}>{value}</p>
    </div>
  );
}
